import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './addBooking.css';
import LoadingPopup from '../../SharedView/LoadingPopup';
import axios from 'axios';
import { DateTime } from 'luxon';
import { registerLocale } from 'react-datepicker';
import enGB from 'date-fns/locale/en-GB';
import BookingPricePreview from './AddBooking/BookingPricePreview';
import ConfirmPopup from '../ConfirmPopup';
import PaymentModeSelector from './AddBooking/PaymentModeSelector';
import EditSettlementSummary from './AddBooking/EditSettlementSummary';
import AddonsSelection from './AddBooking/AddonsSelection';
import { maintenanceTypeIds, addonPayload, assignedToForm, customAddonsValid } from './bookingTypeRules';
import { adminShortfallReason, isAdminWalletShort } from '../../ClientView/walletShortfall';

registerLocale('en-GB', enGB);

const NO_CLEANER_TYPES = [6, 7, 8, 12];
const LAUNDRY_TYPES = [1, 5, 6];
const AMENITIES_TYPES = [1, 5];

const durationOptions = [
  { value: '1', label: '1 Hour' }, { value: '1.5', label: '1.5 Hours' },
  { value: '2', label: '2 Hours' }, { value: '2.5', label: '2.5 Hours' },
  { value: '3', label: '3 Hours' }, { value: '3.5', label: '3.5 Hours' },
  { value: '4', label: '4 Hours' }, { value: '4.5', label: '4.5 Hours' },
  { value: '5', label: '5 Hours' }, { value: '5.5', label: '5.5 Hours' },
  { value: '6', label: '6 Hours' }, { value: '6.5', label: '6.5 Hours' },
  { value: '7', label: '7 Hours' }, { value: '7.5', label: '7.5 Hours' },
  { value: '8', label: '8 Hours' },
];

function EditBooking({ bookingId, onClose }) {
  const [cleaners, setCleaners] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [bookingStatus, setBookingStatus] = useState([]);
  const [unavailableCleaners, setUnavailableCleaners] = useState([]);
  const [addons, setAddons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  // Admin checkout selector on edit (payments Phase 3) — only for UNPAID bookings.
  const [paymentContext, setPaymentContext] = useState(null);
  const [paymentMode, setPaymentMode] = useState('wallet');
  const [paymentModeTouched, setPaymentModeTouched] = useState(false);
  const [liveTotal, setLiveTotal] = useState(0);
  // The price as it stood when the form OPENED — the baseline that decides
  // whether this edit changes the price at all. Captured on the first
  // pricingReady, before the user can have touched anything.
  const [originalTotal, setOriginalTotal] = useState(null);
  // The regenerate/relocate double-confirmation on invoiced bookings (owner,
  // 2026-09-02): a price-changing edit is submitted only after it is confirmed.
  const [confirmRegen, setConfirmRegen] = useState(false);
  const liveTotalRef = useRef(0);
  useEffect(() => { liveTotalRef.current = liveTotal; }, [liveTotal]);
  // Is liveTotal a REAL price yet? liveTotal starts at 0 and 0 is also a legal
  // total, so the number alone cannot answer this — and on an already-paid
  // booking the difference decides whether we offer to refund money
  // (2026-08-30: "Refund to wallet AED 425.25" rendered under "Loading
  // pricing…", with Update Booking enabled).
  const [pricingReady, setPricingReady] = useState(false);
  const [alreadyCharged, setAlreadyCharged] = useState(null); // null = unknown yet
  const [pendingSummary, setPendingSummary] = useState(null);
  const [bookingMode, setBookingMode] = useState('wallet'); // stored payment method
  // How the booking was ACTUALLY settled, from the server. The stored method is
  // an intention; this is what happened — and they differ (a booking marked
  // "invoice later" that a payment link then paid in full, 2026-08-30).
  const [settledVia, setSettledVia] = useState(null);
  // The invoice this booking sits on, when it has one. Drives the notice at
  // the top: an invoiced booking's price cannot be changed casually, because
  // the invoice has to be regenerated and paid money goes back to the wallet
  // (2026-09-01). null = none, or not loaded yet.
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  // Drives the loud banner below: a booking with overrides must never be
  // edited in ignorance of them (2026-08-03).
  const [hasPriceOverride, setHasPriceOverride] = useState(false);
  // The overridden lines themselves, so the warning can name them.
  const [priceOverrides, setPriceOverrides] = useState([]);
  // item_id -> { kind: ''|'%'|'aed', value, reason } — edited inside the
  // Updated Booking Cost table (one table, not two — 2026-08-03).
  const [overrideDraft, setOverrideDraft] = useState({});
  const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
  // Same rule the server enforces in bookingService.canSetPriceOverrides.
  const canEditInvoicedPrice = userRole === 'manager' || userRole === 'supervisor';
  // PAID-booking settlement chooser (mirrors the unpaid checkout selector). The
  // admin-picked rail settles ONLY a price INCREASE; the stored method above is
  // history and never changes.
  const [settleMode, setSettleMode] = useState('wallet');
  const [settleModeTouched, setSettleModeTouched] = useState(false);
  const [hasOpenAutoCharge, setHasOpenAutoCharge] = useState(false); // in-flight card completion → lock chooser

  const [formData, setFormData] = useState({
    booking_start: null, booking_end: null,
    client_id: '', property_id: '', type_id: '', status_id: '',
    pending_reason: '', booking_specific_notes: '', booking_warehouse_notes: '',
    required_cleaners: 1, duration: 2, cleaner_ids: [],
    is_laundry: false, is_amenities: false, selected_addons: [],
    custom_price_excl_vat: '',
  });

  const navigate = useNavigate();
  const isNoCleanerType = NO_CLEANER_TYPES.includes(formData.type_id);
  // Maintenance-like types (Maintenance, Swimming Pool Maintenance): looked
  // up by name so we don't have to hardcode an ID. Hides cleaners +
  // laundry/amenities toggles and defaults duration to 1h.
  const maintenanceIds = maintenanceTypeIds(bookingTypes);
  const isMaintenanceType = maintenanceIds.includes(formData.type_id);
  // The invoice's main line for this type: "Maintenance Service" or
  // "Swimming Pool Maintenance Service" (items 808 / 1350).
  const maintenanceLineLabel = `${bookingTypes.find(t => t.id === formData.type_id)?.type || 'Maintenance'} Service`;
  const isPending = bookingStatus.find(s => s.id === formData.status_id)?.status_name === 'Pending';
  const set = (u) => setFormData(p => ({ ...p, ...u }));

  // --- Fetch data ---
  useEffect(() => {
    if (!bookingId) return;
    (async () => {
      try {
        setIsLoading(true);
        const bRes = await axios.get(`/api/bookings/details/${bookingId}`);
        const d = bRes.data;
        setBookingMode(d.payment_mode || 'wallet');
        setHasPriceOverride(!!d.has_price_override);
        setPriceOverrides(Array.isArray(d.price_overrides) ? d.price_overrides : []);
        // Rehydrate the drafts so the table opens showing what stands.
        //
        // EACH FIELD ON ITS OWN. This used to assume that "not a percent" meant
        // "a rate", so a QUANTITY-ONLY override — both price fields null — was
        // hydrated as { kind:'aed', value:String(parseFloat(null)) }, i.e.
        // "NaN". The preview then read that as an exact rate, fell through
        // `parseFloat('NaN') || 0`, and rendered the line at AED 0.00 tagged
        // Custom over a booking whose stored rate was untouched (2026-08-30).
        // A saved quantity override must come back as a quantity, and a line
        // with no price override must come back as List price.
        const od = {};
        for (const o of (d.price_overrides || [])) {
            const hasPct = o.discount_percent != null;
            const hasRate = o.custom_rate != null;
            const hasQty = o.custom_quantity != null;
            od[o.item_id] = {
                kind: hasPct ? '%' : hasRate ? 'aed' : '',
                value: hasPct ? String(parseFloat(o.discount_percent))
                     : hasRate ? String(parseFloat(o.custom_rate)) : '',
                qty: hasQty ? String(parseFloat(o.custom_quantity)) : '',
                qtyTouched: hasQty,
                reason: o.reason || '',
            };
        }
        setOverrideDraft(od);
        const aRes = await axios.get(`/api/bookings/addons/${bookingId}`);

        setFormData(p => ({
          ...p, client_id: d.client_id || '', property_id: d.property_id || '',
          type_id: d.type_id || '', status_id: d.status_id || '',
          pending_reason: d.pending_reason || '',
          booking_specific_notes: d.booking_specific_notes || '',
          booking_warehouse_notes: d.booking_warehouse_notes || '',
          required_cleaners: d.required_cleaners || 1, duration: d.duration || 2,
          cleaner_ids: d.cleaner_ids || [],
          booking_start: d.booking_start ? DateTime.fromISO(d.booking_start, { zone: 'Asia/Dubai' }).toJSDate() : null,
          booking_end: d.booking_end ? DateTime.fromISO(d.booking_end, { zone: 'Asia/Dubai' }).toJSDate() : null,
          is_laundry: d.is_laundry || false, is_amenities: d.is_amenities || false,
          custom_price_excl_vat: d.custom_price_excl_vat != null ? String(d.custom_price_excl_vat) : '',
          selected_addons: aRes.data.assignedAddons.map(assignedToForm),
        }));

        const [clR, pR, crR, tR, sR] = await Promise.all([
          axios.get('/api/clients'), axios.get(`/api/properties?clientId=${d.client_id}`),
          axios.get('/api/team/role/?role=cleaner'), axios.get('/api/bookings/booking_types'),
          axios.get('/api/bookings/booking_status'),
        ]);
        setClients(clR.data); setProperties(pR.data); setCleaners(crR.data);
        setBookingTypes(tR.data); setBookingStatus(sR.data); setAddons(aRes.data.allAddons);

        // Payments Phase 3: card availability + wallet, this client's pending
        // items (informational), and how much is ALREADY charged for THIS booking
        // (decides whether the checkout selector applies). All best-effort.
        if (d.client_id) {
          // Separate entity wallets: the wallet THIS booking charges (its property's).
          axios.get(`/api/bookings/admin/payment-context/${d.client_id}`, { params: { booking_id: bookingId } })
            .then((r) => setPaymentContext(r.data)).catch(() => setPaymentContext(null));
          axios.get(`/api/bookings/admin/pending-summary/${d.client_id}`)
            .then((r) => setPendingSummary(r.data)).catch(() => setPendingSummary(null));
          // TRUE settled total for this booking. The /wallet/clients/:id/transactions
          // endpoint hides card charges and would mislabel a card-paid booking as
          // unpaid (payments Phase 3).
          //
          // DIRECT CARD RAIL (2026-07-18). `net_charged` is the WALLET net only.
          // A booking paid on the direct rail writes booking_payment_receipts and
          // NO wallet_transactions, so net_charged is 0.00 for it — which made
          // `isUnpaid` true and showed "Previously charged AED 0.00" over a booking
          // the client had fully paid for by card. The server now also returns
          // `settled_total` (= wallet net + receipts − refunds, getBookingSettledTotal).
          // Prefer it; fall back to net_charged only for a server that predates the
          // field, which is exactly the old behaviour rather than a guess.
          axios.get(`/api/bookings/admin/net-charged/${bookingId}`)
            .then((r) => {
              const settled = Number(r.data?.settled_total);
              setAlreadyCharged(Number.isFinite(settled) ? settled : parseFloat(r.data?.net_charged || 0));
              setHasOpenAutoCharge(!!r.data?.has_open_auto_charge);
              setSettledVia(r.data?.settlement || null);
              setInvoiceInfo(r.data?.invoice || null);
              /* PRICE BASELINE FROM THE SERVER (owner, 2026-09-02: the popup
                 claimed a wrong delta twice). The live preview races the
                 admin's first clicks — a baseline captured mid-edit blamed
                 the edit for add-ons that were already on the booking. The
                 server's stored total arrives with invoiceInfo, which is the
                 only consumer of the baseline, so they can't disagree. */
              const st = Number(r.data?.stored_total);
              if (Number.isFinite(st)) setOriginalTotal(st);
            })
            .catch(() => setAlreadyCharged(0));
        }
      } catch (e) { console.error(e); } finally { setIsLoading(false); }
    })();
  }, [bookingId]);

  // Pre-select the checkout mode for an unpaid booking: wallet if it covers the
  // due amount → otherwise INVOICE LATER (owner 2026-07-20 — no wallet balance
  // defaults to invoice-later, not the saved card; charging the card is a
  // deliberate MANUAL staff choice). Until the admin overrides.
  useEffect(() => {
    if (!paymentContext || paymentModeTouched) return;
    const available = Number(paymentContext.wallet?.available_balance || 0);
    const due = liveTotal - (alreadyCharged || 0);
    // AN EMPTY WALLET IS NEVER THE DEFAULT RAIL (2026-08-03): even when
    // nothing is due — a 0-AED change, or a booking priced to 0 by an
    // override — defaulting to "Wallet · AED 0.00 available" reads like the
    // wallet is about to be used. It also closed a float edge: a due of a
    // fraction of a fil passed `available >= due - 0.01` with 0 balance.
    if (available <= 0.005) setPaymentMode('invoice_later');
    else if (due > 0 && available >= due - 0.01) setPaymentMode('wallet');
    else if (due > 0) setPaymentMode('invoice_later');
    else setPaymentMode('wallet');
  }, [paymentContext, liveTotal, alreadyCharged, paymentModeTouched]);

  // Pre-select the PAID-booking settlement rail, until the admin overrides.
  // Owner 2026-07-21: default to INVOICE LATER (the increase rides the next
  // invoice — NO price adjustment is raised), and auto-pick WALLET only when the
  // client's available balance covers the increase. `available_balance` already
  // excludes reserved-for-pending-request money (walletUtils) — exactly the
  // owner's "available excluding pending requests" — and never nets invoices or
  // adjustments into it. A wallet-covered increase charges immediately (still no
  // adjustment); everything else simply waits for the invoice.
  useEffect(() => {
    if (settleModeTouched) return;
    const available = Number(paymentContext?.wallet?.available_balance || 0);
    const due = (liveTotal || 0) - (alreadyCharged || 0);
    // Same empty-wallet rule as the unpaid rail below (2026-08-03).
    if (available <= 0.005) setSettleMode('invoice_later');
    else if (due > 0.005 && available >= due - 0.01) setSettleMode('wallet');
    else setSettleMode('invoice_later');
  }, [settleModeTouched, paymentContext, liveTotal, alreadyCharged]);

  // The selector only applies to an UNPAID booking (nothing net-charged yet).
  const isUnpaid = alreadyCharged !== null && alreadyCharged <= 0.01;
  // Everything the settlement decision rests on. Nothing may be saved until
  // both are in: an unknown paid-state is exactly how an edit ends up refunding
  // or charging against a booking whose payments had not loaded yet.
  const settlementKnown = pricingReady && alreadyCharged !== null;

  // ZERO FLOOR (P4). Same rule as Add Booking: a wallet-mode update the wallet
  // cannot cover would take AVAILABLE below zero, so Update is disabled with the
  // shortfall as its reason rather than failing on click. Only the UNPAID rail
  // is gated here — the PAID rail settles a difference through
  // EditSettlementSummary, which already offers the price-adjustment escape
  // hatch and must stay usable (a refund path must never be blocked).
  const walletFloorBlocks = isUnpaid
    && paymentMode === 'wallet'
    && !!paymentContext
    && isAdminWalletShort({
      cost: liveTotal - (alreadyCharged || 0),
      available: Number(paymentContext.wallet?.available_balance || 0),
    });
  const walletFloorReason = walletFloorBlocks
    ? adminShortfallReason({
      cost: liveTotal - (alreadyCharged || 0),
      available: Number(paymentContext.wallet?.available_balance || 0),
    })
    : null;

  useEffect(() => {
    if (!formData.booking_start || !formData.booking_end) return;
    (async () => {
      try {
        // Dubai wall time for the availability query (item 8): formData holds
        // ISO strings with an offset, so device-local getters sent the wrong
        // wall time from any non-Dubai machine. Same pattern as handleSubmit.
        const fmt = (d) => DateTime.fromJSDate(new Date(d)).setZone('Asia/Dubai').toFormat('yyyy-MM-dd HH:mm:ss');
        const res = await axios.get('/api/bookings/cleaners/availability', {
          params: { booking_start: fmt(formData.booking_start), booking_end: fmt(formData.booking_end), selected_booking_id: bookingId },
        });
        setUnavailableCleaners(res.data.map(c => c.cleaner_id));
      } catch (e) { console.error(e); }
    })();
  }, [formData.booking_start, formData.booking_end, bookingId]);

  // --- Handlers ---
  const getDubaiDate = (input) => {
    if (!input) return null;
    const dt = typeof input === 'string' ? DateTime.fromISO(input, { zone: 'Asia/Dubai' }) : DateTime.fromJSDate(input, { zone: 'Asia/Dubai' });
    return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second);
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const local = DateTime.fromJSDate(date);
    const dubai = DateTime.fromObject({ year: local.year, month: local.month, day: local.day, hour: local.hour, minute: local.minute }, { zone: 'Asia/Dubai' });
    if (!dubai.isValid) return;
    const end = isNoCleanerType ? dubai : dubai.plus({ hours: formData.duration });
    set({ booking_start: dubai.toISO(), booking_end: end.toISO() });
  };

  const handleDurationChange = (opt) => {
    const dur = parseFloat(opt.value);
    const end = formData.booking_start ? new Date(new Date(formData.booking_start).getTime() + dur * 3600000) : null;
    set({ duration: dur, booking_end: end });
  };

  // The overrides exactly as the server wants them. One builder, used by the
  // SAVE — the price panel no longer writes anything of its own.
  const overridesForSubmit = () => {
    const isPrice = (d) => (d.kind === '%' || d.kind === 'aed') && d.value !== '';
    const isQty = (d) => d.qty !== '' && d.qty != null && parseFloat(d.qty) > 0;
    return Object.entries(overrideDraft)
      .filter(([, d]) => isPrice(d) || isQty(d))
      .map(([item_id, d]) => ({
        item_id: parseInt(item_id, 10),
        ...(isPrice(d)
          ? (d.kind === '%' ? { discount_percent: parseFloat(d.value) } : { custom_rate: parseFloat(d.value) })
          : {}),
        ...(isQty(d) ? { custom_quantity: parseFloat(d.qty) } : {}),
        ...(d.reason ? { reason: d.reason } : {}),
      }));
  };

  /* Does THIS save change the price of an INVOICED booking? Decided from the
     live preview against the price the form opened with. The server stays the
     authority (its gate compares trigger-written totals), but the question has
     to be asked BEFORE submitting, and the preview is what the user is looking
     at. */
  const priceChangesInvoiced = () =>
    invoiceInfo && pricingReady && originalTotal != null
    && Math.abs((liveTotal || 0) - originalTotal) > 0.009;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.booking_start) { alert('Please select a start date/time.'); return; }
    if (!customAddonsValid(formData.selected_addons)) {
      alert('Each custom add-on needs a name, a price (0 or more) and a quantity.');
      return;
    }
    /* DOUBLE CONFIRMATION on invoiced bookings (owner, 2026-09-02): even a
       manager or supervisor must confirm that the edit regenerates the invoice
       — and, when it is paid, that the payment funds will be relocated. The
       popup's Confirm calls back into this same handler with the flag set. */
    if (priceChangesInvoiced() && !confirmRegen) {
      setConfirmRegen(true);
      return;
    }
    setConfirmRegen(false);
    try {
      setIsLoading(true);
      const startStr = DateTime.fromJSDate(new Date(formData.booking_start)).setZone('Asia/Dubai').toFormat('yyyy-MM-dd HH:mm:ss');
      const endStr = formData.booking_end ? DateTime.fromJSDate(new Date(formData.booking_end)).setZone('Asia/Dubai').toFormat('yyyy-MM-dd HH:mm:ss') : null;
      const res = await axios.put(`/api/bookings/${bookingId}`, {
        // Part of THIS save. Sent always (an empty array clears them), so
        // pressing Update is the only thing that changes a price or a quantity
        // — and Cancel genuinely cancels (2026-08-30).
        price_overrides: overridesForSubmit(),
        ...formData, required_cleaners: (isNoCleanerType || isMaintenanceType) ? 0 : formData.required_cleaners,
        cleaner_ids: isMaintenanceType ? [] : formData.cleaner_ids.filter(id => id),
        // Net price entered by admin for Maintenance bookings — null otherwise.
        custom_price_excl_vat: isMaintenanceType
          ? (parseFloat(formData.custom_price_excl_vat) || null)
          : null,
        booking_start: startStr, booking_end: endStr,
        selected_addons: formData.selected_addons.map(addonPayload),
        // UNPAID → the checkout selector. PAID + increase → the admin's chosen
        // settlement rail. No increase → undefined so the backend no-ops (the
        // decrease keeps the refund-to-wallet path keyed on the stored method).
        payment_mode: isUnpaid
          ? paymentMode
          : ((liveTotal - (alreadyCharged || 0)) > 0.005 ? settleMode : undefined),
      });
      // A saved booking is NOT the same as a charged booking — reflect what
      // actually happened.
      const wallet = res.data?.wallet;

      // WHAT THE EDIT DID *NOT* DO (2026-08-30). Every branch below reports
      // the money outcome, and not one of them mentioned that a manually set
      // line kept its value — so "Booking updated successfully!" was the whole
      // story on a save where the hours changed and the charge for that line
      // deliberately did not follow.
      //
      // Read from the draft that was just SUBMITTED, not the state the dialog
      // opened with: clearing an override would otherwise still claim it held,
      // and a newly added one would go unmentioned.
      const isPrice = (dr) => (dr.kind === '%' || dr.kind === 'aed') && dr.value !== '';
      const isQty = (dr) => dr.qty !== '' && dr.qty != null && parseFloat(dr.qty) > 0;

      const held = Object.entries(overrideDraft).filter(([, dr]) => isPrice(dr) || isQty(dr));
      const heldNames = held
        .map(([id]) => (priceOverrides.find((o) => String(o.item_id) === String(id)) || {}).item_name)
        .filter(Boolean);

      let heldNote = '';
      if (held.length > 0) {
        const anyQty = held.some(([, dr]) => isQty(dr));
        const anyPrice = held.some(([, dr]) => isPrice(dr));
        const what = anyQty && anyPrice ? 'price and quantity' : anyQty ? 'quantity' : 'price';
        // Name the lines when every one of them is known; otherwise count them.
        const who = heldNames.length === held.length
          ? `${heldNames.join(', ')} ${held.length === 1 ? 'keeps its' : 'keep their'}`
          : `${held.length} line${held.length === 1 ? '' : 's'} keep${held.length === 1 ? 's' : ''} their`;
        heldNote = ` ${who} manually set ${what} — that was not recalculated from this edit.`;
      }

      // A failed Zoho follow-through must never hide under a clean success
      // line (2026-09-02) — whatever the outcome, say what a human still has
      // to do.
      const syncWarn = wallet && wallet.zoho_sync && wallet.zoho_sync.ok === false && wallet.zoho_sync.manual_fix
        ? ` ⚠️ Zoho needs attention: ${wallet.zoho_sync.manual_fix}`
        : '';
      // Every outcome below carries them, so the one nobody remembers to
      // update cannot be the one that stays silent.
      const say = (m) => setFeedbackMessage(m + heldNote + syncWarn);

      if (wallet && wallet.adjustment_created) {
        // Reprice-up on an already-paid booking → the extra becomes a charge
        // adjustment the client approves/disputes, not a silent wallet debit.
        const amt = Number(wallet.adjustment_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        say(`Booking updated. A charge adjustment of AED ${amt} was created — the client will be asked to approve or dispute it (auto-charged after 3 days if a card is saved).`);
      } else if (wallet && wallet.charged === true && wallet.via === 'card') {
        say('Booking updated and paid by the client’s saved card.');
      } else if (wallet && wallet.charged === false && wallet.reason === 'invoice_later') {
        // Say the resulting number, not "the difference" — after several edits
        // the open balance is what the admin actually needs (2026-09-02).
        const owedNow = Math.max(0, (liveTotalRef.current || 0) - (alreadyCharged || 0));
        const owedStr = owedNow.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        say(invoiceInfo
          ? `Booking updated — invoice ${invoiceInfo.number} was regenerated; AED ${owedStr} is open on it to collect (no payment taken).`
          : 'Booking updated — to be invoiced later (no payment taken).');
      } else if (wallet && wallet.charged === false && wallet.via === 'card') {
        const map = {
          no_active_card: 'no active saved card',
          declined: 'the card was declined',
          gateway_unavailable: 'card charging is unavailable',
          wallet_post_failed: 'the card was charged but the wallet ledger needs reconciling (tech notified)',
        };
        say(`⚠️ Booking saved, but card payment did not complete — ${map[wallet.reason] || 'card payment failed'}. The booking was set to Pending.`);
      } else if (wallet && wallet.charged === false) {
        say(wallet.reason === 'deposit_sync_error'
          ? '⚠️ Booking saved, but the client was NOT charged — wallet deposit sync error (contact tech support). The booking was set to Pending.'
          : '⚠️ Booking saved, but the client was NOT charged — insufficient wallet balance. The booking was set to Pending.');
      } else if (wallet && wallet.success === true && wallet.cash_charged !== undefined) {
        // Paid-booking increase settled straight from the wallet (chooser picked wallet).
        const charged = Number(wallet.cash_charged || 0) + Number(wallet.bonus_charged || 0);
        const amt = charged.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        say(`Booking updated — AED ${amt} was charged from the client’s wallet.`);
      } else {
        say('Booking updated successfully!');
      }
    // Surface the backend's own message (validation / guard refusals) instead of
    // a blind generic — mirrors PendingDeposits/PendingPayoutsSection.
    } catch (e) { console.error(e); setFeedbackMessage(e.response?.data?.error || 'Error updating booking.'); }
    finally { setIsLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try { setIsLoading(true); await axios.delete(`/api/bookings/${bookingId}`); setFeedbackMessage('Booking deleted successfully!'); }
    catch (e) { console.error(e); setFeedbackMessage('Error deleting booking.'); }
    finally { setIsLoading(false); }
  };

  if (isLoading) {
    return (<div className="popup-overlay"><div className="edit-booking-popup" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LoadingPopup isLoading={true} /></div></div>);
  }

  return (
    <div className="popup-overlay">
      <div className="edit-booking-popup">
        <button type="button" className="modal-x" onClick={onClose}>&times;</button>
        <h2>Edit Booking</h2>

        {/* NAME THE LINE, AND SAY THE PRICE HOLDS (2026-08-30). This used to
            warn only that an override "can" stop applying, which told nobody
            what to do. It now says which line was set by hand and that editing
            leaves that price alone — the thing an admin needs to know before
            they change hours or cleaners and expect the total to follow. */}
        {/* ALREADY INVOICED. Said before anything is typed, not after Update is
            pressed, because the consequence is money moving. Managers and
            supervisors see what will happen; admins see that it is not theirs
            to do. Either way every non-price edit stays open, which is why the
            wording is careful to say "price" and not "this booking". */}
        {invoiceInfo && (
          <div className="invoiced-edit-warning">
            <strong>
              This booking is already invoiced ({invoiceInfo.number}).
            </strong>
            <div className="invoiced-edit-warning__body">
              {canEditInvoicedPrice ? (
                invoiceInfo.is_paid ? (
                  invoiceInfo.is_guest
                    ? 'A price change will regenerate this invoice. It was paid by a guest, so any money owed '
                      + 'back cannot go to the client\u2019s wallet and must be returned by hand.'
                    : 'A price change will regenerate this invoice and settle the difference \u2014 extra '
                      + 'to pay is charged to the client, money owed back goes to their wallet. You will '
                      + 'be asked to confirm.'
                ) : (
                  'A price change will regenerate this invoice. You will be asked to confirm.'
                )
              ) : (
                <>
                  Changing the price would require the invoice to be regenerated
                  {invoiceInfo.is_paid ? ', and money already paid returned to the client\u2019s wallet' : ''}.
                  {' '}<strong>Only a manager or supervisor can do that.</strong>{' '}
                  {/* Cancelling or rescheduling reprices to the penalty amount, so it
                      is barred by the same rule. Saying "every other change is open to
                      you" without this would be a promise the server then breaks. */}
                  That includes cancelling or rescheduling it, which reprices the
                  booking. Every other change is still open to you.
                </>
              )}
            </div>
          </div>
        )}

        {hasPriceOverride && (
          <div className="override-edit-warning">
            {/* Say which it is. A quantity-only override changes the HOURS, not
                the rate, and calling it "a manually set price" over an
                untouched price is how somebody goes looking for a discount
                that was never applied (2026-08-30). */}
            <strong>
              {(() => {
                /* READ THE STAGED OVERRIDES TOO (2026-08-30). The banner is
                   raised by has_price_override, which Done sets the moment
                   something is staged — but priceOverrides holds only what is
                   SAVED, and is still empty at that point. Mapping an empty
                   list gave an empty noun: "This booking has manually set ."
                   Fall back to the staged drafts, and never emit the sentence
                   without something to put in it. */
                const staged = overridesForSubmit();
                const src = priceOverrides.length ? priceOverrides : staged;
                const one = src.length === 1 ? src[0] : null;
                const kindOf = (o) => (o.discount_percent != null || o.custom_rate != null)
                  ? (o.custom_quantity != null ? 'price and quantity' : 'price')
                  : 'quantity';
                if (one && one.item_name) return `${one.item_name} has a manually set ${kindOf(one)}.`;
                const kinds = [...new Set(src.map(kindOf))];
                if (!kinds.length) return 'This booking has a manually set price.';
                return `This booking has manually set ${kinds.join(' and ')}.`;
              })()}
            </strong>{' '}
            {priceOverrides.length > 1 && priceOverrides.some((o) => o.item_name) && (
              <>({priceOverrides.map((o) => o.item_name).filter(Boolean).join(', ')}){' '}</>
            )}
            Editing the booking does not change it — the price stays as it was
            set. Contact a manager if the price itself needs to change.
            {priceOverrides.some((o) => o.reason) && (
              <div className="override-edit-warning__reason">
                Reason: {priceOverrides.map((o) => o.reason).filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        )}

        <form className="new-booking-form" onSubmit={handleSubmit}>

          {/* Card 1: Type, Status + Toggles inline */}
          <div className="ab-card">
            <div className="ab-card__head"><h3 className="ab-card__title">Booking Details</h3></div>
            <div className="form-row">
              <div className="form-group">
                <label>Booking Type</label>
                <Select options={bookingTypes.map(t => ({ value: t.id, label: t.type }))}
                  value={bookingTypes.map(t => ({ value: t.id, label: t.type })).find(o => o.value === formData.type_id) || null}
                  onChange={(opt) => {
                    const typeId = opt ? opt.value : '';
                    const isMaintenance = maintenanceIds.includes(typeId);
                    set({ type_id: typeId,
                      required_cleaners: isMaintenance ? 0 : (NO_CLEANER_TYPES.includes(typeId) ? 0 : Math.max(1, formData.required_cleaners)),
                      cleaner_ids: isMaintenance || NO_CLEANER_TYPES.includes(typeId) ? [] : formData.cleaner_ids,
                      duration: isMaintenance ? 1 : (NO_CLEANER_TYPES.includes(typeId) ? 0 : formData.duration),
                      is_laundry: isMaintenance ? false : (LAUNDRY_TYPES.includes(typeId) ? true : formData.is_laundry),
                      is_amenities: isMaintenance ? false : (AMENITIES_TYPES.includes(typeId) ? true : formData.is_amenities),
                      // Clear custom price when switching away from Maintenance.
                      custom_price_excl_vat: isMaintenance ? formData.custom_price_excl_vat : '' });
                  }} placeholder="Select type" />
              </div>
              <div className="form-group">
                <label>Status</label>
                <Select options={bookingStatus.map(s => ({ value: s.id, label: s.status_name }))}
                  value={bookingStatus.map(s => ({ value: s.id, label: s.status_name })).find(o => o.value === formData.status_id) || null}
                  onChange={(opt) => set({ status_id: opt ? opt.value : '', pending_reason: (opt?.label === 'Pending') ? formData.pending_reason : '' })}
                  placeholder="Select status" />
              </div>
              {isMaintenanceType && (
                <div className="form-group">
                  <label>Price (excl. VAT, AED)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.custom_price_excl_vat}
                    onChange={(e) => set({ custom_price_excl_vat: e.target.value })}
                    placeholder="e.g. 250.00"
                    required
                  />
                  {/* The VAT-inclusive figure right where the price is typed
                      (owner, 2026-10-04) — what the client actually pays for
                      the service line, before any add-ons. */}
                  <div className="ab-vatIncl">
                    {parseFloat(formData.custom_price_excl_vat) > 0
                      ? <>Incl. VAT (5%): <strong>AED {(Math.round(parseFloat(formData.custom_price_excl_vat) * 105) / 100).toFixed(2)}</strong></>
                      : 'VAT (5%) is added on the Zoho invoice.'}
                  </div>
                </div>
              )}
              {!isMaintenanceType && (
                <div className="form-group laundry">
                  <label>Laundry</label>
                  <label className="switch"><input type="checkbox" checked={formData.is_laundry} onChange={() => set({ is_laundry: !formData.is_laundry })} /><span className="slider round"></span></label>
                </div>
              )}
              {!isMaintenanceType && (
                <div className="form-group amenities">
                  <label>Amenities</label>
                  <label className="switch"><input type="checkbox" checked={formData.is_amenities} onChange={() => set({ is_amenities: !formData.is_amenities })} /><span className="slider round"></span></label>
                </div>
              )}
            </div>
            {isPending && (<div style={{ marginTop: 14, maxWidth: 400 }}><div className="form-group"><label>Pending Reason</label>
              <input type="text" name="pending_reason" value={formData.pending_reason} onChange={(e) => set({ pending_reason: e.target.value })} placeholder="Enter pending reason" required /></div></div>)}
          </div>

          {/* Card 2: Client & Property */}
          <div className="ab-card">
            <div className="ab-card__head"><h3 className="ab-card__title">Client & Property</h3></div>
            <div className="ab-grid">
              <div className="form-group"><label>Client</label>
                <Select options={clients.map(c => ({ value: c.id, label: c.display_name || `${c.first_name} ${c.last_name}` }))}
                  value={clients.map(c => ({ value: c.id, label: c.display_name || `${c.first_name} ${c.last_name}` })).find(o => o.value === formData.client_id) || null}
                  isDisabled /></div>
              <div className="form-group"><label>Property</label>
                <Select options={properties.map(p => ({ value: p.id, label: `${p.unit_number} - ${p.address_line_1} (${p.bedrooms === 0 ? 'S' : p.bedrooms}, ${p.bathrooms})` }))}
                  value={properties.map(p => ({ value: p.id, label: `${p.unit_number} - ${p.address_line_1} (${p.bedrooms === 0 ? 'S' : p.bedrooms}, ${p.bathrooms})` })).find(o => o.value === formData.property_id) || null}
                  isDisabled /></div>
            </div>
          </div>

          {/* Card 3: Schedule & Cleaners */}
          <div className="ab-card">
            <div className="ab-card__head"><h3 className="ab-card__title">Schedule & Cleaners</h3></div>
            <div className={(isNoCleanerType || isMaintenanceType) ? 'ab-grid' : 'ab-grid--3'}>
              {!isNoCleanerType && !isMaintenanceType && (<div className="form-group"><label>Required Cleaners</label>
                <input type="number" value={formData.required_cleaners} onChange={(e) => set({ required_cleaners: parseInt(e.target.value, 10) || 1 })} min="1" required /></div>)}
              <div className="form-group"><label>Start Time</label>
                <DatePicker selected={getDubaiDate(formData.booking_start)} onChange={handleDateChange}
                  showTimeSelect timeFormat="HH:mm" timeIntervals={30} timeCaption="Time"
                  locale="en-GB" dateFormat="dd/MM/yyyy HH:mm" placeholderText="Select Start Time" required /></div>
              {!isNoCleanerType && (<div className="form-group"><label>Duration</label>
                <Select options={durationOptions} value={durationOptions.find(o => o.value === String(formData.duration)) || null}
                  onChange={handleDurationChange} placeholder="Select duration" /></div>)}
            </div>
            {!isNoCleanerType && !isMaintenanceType && formData.required_cleaners > 0 && (
              <div className="ab-grid--4" style={{ marginTop: 14 }}>
                {Array.from({ length: formData.required_cleaners }).map((_, i) => (
                  <div key={i} className="form-group"><label>Cleaner {i + 1}</label>
                    <Select options={cleaners.map(c => ({
                        value: c.id, label: `${c.first_name} ${c.last_name}${unavailableCleaners.includes(c.id) && !formData.cleaner_ids.includes(c.id) ? ' (Unavailable)' : ''}`,
                        isDisabled: unavailableCleaners.includes(c.id) && !formData.cleaner_ids.includes(c.id),
                      }))}
                      value={cleaners.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name}` })).find(o => o.value === formData.cleaner_ids[i]) || null}
                      onChange={(opt) => { const ids = [...formData.cleaner_ids]; ids[i] = opt ? opt.value : null; set({ cleaner_ids: ids }); }}
                      placeholder={`Cleaner ${i + 1}`} isClearable /></div>
                ))}
              </div>
            )}
          </div>

          {/* Card 4: Add-ons & Notes */}
          <div className="ab-card">
            <div className="ab-card__head"><h3 className="ab-card__title">Add-ons & Notes</h3></div>
            {/* Same picker as Add Booking: catalogue add-ons + custom add-ons
                (typed name and net price, shown as such on the invoice). */}
            <AddonsSelection
              addons={addons.map(a => ({ id: a.item_id, name: a.name, default_price: a.default_price }))}
              formData={formData}
              setFormData={setFormData}
            />
            <div className="notes" style={{ marginTop: 14 }}>
              <div className="form-group"><label>Booking Notes</label>
                <textarea value={formData.booking_specific_notes} onChange={(e) => set({ booking_specific_notes: e.target.value })} placeholder="Specific notes for this booking" /></div>
              <div className="form-group"><label>Warehouse Notes</label>
                <textarea value={formData.booking_warehouse_notes} onChange={(e) => set({ booking_warehouse_notes: e.target.value })} placeholder="Warehouse notes for this booking" /></div>
            </div>
          </div>

          {/* Price Preview — always shown */}
          <BookingPricePreview
            selectedAddons={formData.selected_addons} clientId={formData.client_id}
            propertyId={formData.property_id} properties={properties} bookingId={bookingId}
            typeId={formData.type_id} statusId={formData.status_id}
            bookingStart={formData.booking_start} bookingEnd={formData.booking_end}
            requiredCleaners={formData.required_cleaners} isAmenities={formData.is_amenities}
            duration={formData.duration}
            isMaintenance={isMaintenanceType}
            maintenanceLabel={maintenanceLineLabel}
            customPriceExclVat={formData.custom_price_excl_vat}
            onTotal={setLiveTotal}
            onReady={setPricingReady}
            // Quantity is editable inside the price panel too, so changing a
            // count and its rate is one visit rather than two (2026-08-30).
            // Same state the Add-ons block writes — the two stay in step.
            onQuantityChange={(addonId, qty) => set({
              selected_addons: formData.selected_addons.map(
                (a) => (a.id === addonId ? { ...a, quantity: qty } : a)
              ),
            })}
            showWallet={false}
            /* Managers and supervisors, never admins (2026-08-31). The server
               enforces the same rule; this only decides whether the control is
               offered. */
            canOverride={userRole === 'manager' || userRole === 'supervisor'}
            overrides={overrideDraft}
            onOverridesChange={setOverrideDraft}
            /* NOT a server write any more. Done just closes the editor; the
               numbers stay in the draft until Update Booking saves the whole
               edit in one go. This used to PUT immediately and settle money
               before the manager had committed to anything (2026-08-30). */
            onApplyOverrides={async () => {
              setHasPriceOverride(overridesForSubmit().length > 0);
              return { staged: true };
            }} />

          {/* Payment — mirrors Add Booking. An UNPAID booking gets the interactive
              checkout selector; a PAID booking shows how the price difference
              settles (charge / refund-to-wallet / adjustment) by its method. */}
          <div className="ab-card">
            <div className="ab-card__head"><h3 className="ab-card__title">Payment</h3></div>
            {alreadyCharged === null ? (
              <div className="ab-pendingPrice">
                Checking what has already been paid for this booking…
              </div>
            ) : (
              <>
              {!pricingReady ? (
                // No settlement figure until the price is real. Anything shown
                // here before that is arithmetic on a number still being fetched.
                <div className="ab-pendingPrice">
                  Working out the price… the charge or refund appears once it is known.
                </div>
              ) : isUnpaid ? (
                <PaymentModeSelector
                  mode={paymentMode}
                  onChange={(m) => { setPaymentMode(m); setPaymentModeTouched(true); }}
                  context={paymentContext}
                  total={liveTotal - (alreadyCharged || 0)}
                />
              ) : (
                <EditSettlementSummary
                  bookingMode={bookingMode}
                  invoiceNumber={invoiceInfo?.number}
                  mode={settleMode}
                  onChange={(m) => { setSettleMode(m); setSettleModeTouched(true); }}
                  diff={liveTotal - (alreadyCharged || 0)}
                  available={paymentContext?.wallet?.available_balance}
                  context={paymentContext}
                  disabled={hasOpenAutoCharge}
                  settledVia={settledVia}
                />
              )}
              </>
            )}
          </div>

          {/* Informational only — no gate on edit. */}
          {pendingSummary && ((pendingSummary.counts?.requests || 0) + (pendingSummary.counts?.bookings || 0) > 0) && (
            <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: '#92400e', background: 'rgba(245,158,11,0.09)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 11px' }}>
              ℹ️ This client has {(pendingSummary.counts?.requests || 0) + (pendingSummary.counts?.bookings || 0)} other pending item(s) awaiting review.
            </div>
          )}

          <div className="ab-actions">
            {/* TWO things must be known before this can be pressed (2026-08-30).
                The price, and whether the booking has already been PAID —
                alreadyCharged starts null and arrives from /net-charged on its
                own schedule. Gating only on the price still left a window where
                Update was live while "has this been paid?" was unanswered, and
                that question is what decides charge vs refund. */}
            <button
              type="submit"
              className="submit-button"
              disabled={walletFloorBlocks || !settlementKnown}
              title={walletFloorBlocks ? walletFloorReason
                : (!settlementKnown ? 'Waiting for the price and payment status' : undefined)}
            >
              {settlementKnown ? 'Update Booking'
                : (!pricingReady ? 'Loading price…' : 'Checking payment status…')}
            </button>
          </div>
        </form>

        <ConfirmPopup
          isOpen={confirmRegen}
          title="Regenerate the invoice?"
          isDestructive
          confirmLabel="Yes, update the booking"
          message={invoiceInfo ? (() => {
            /* SAY WHAT THE MONEY DOES, from the numbers the backend settles
               by (owner, 2026-09-02: a lower-price edit on an under-collected
               invoice claimed a refund the backend would never make). Price
               direction comes from the server baseline; money movement from
               what was actually CHARGED — a refund exists only below it, and
               a wallet-rail charge settles the WHOLE outstanding, not just
               this edit's delta. */
            const fmt = (n) => Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const delta = (liveTotal || 0) - (originalTotal || 0);
            const charged = alreadyCharged || 0;
            const owedAfter = Math.max(0, (liveTotal || 0) - charged);
            const refund = Math.max(0, charged - (liveTotal || 0));
            const rail = isUnpaid ? paymentMode : settleMode;
            const inv = invoiceInfo.number;
            const dir = delta > 0
              ? `This edit RAISES the price by AED ${fmt(delta)}`
              : `This edit LOWERS the price by AED ${fmt(delta)}`;
            if (refund > 0.005) {
              return invoiceInfo.is_guest
                ? `${dir}, so invoice ${inv} will be regenerated — AED ${fmt(refund)} is owed back, and a guest payment cannot go to the client’s wallet: return it by hand.`
                : `${dir}, so invoice ${inv} will be regenerated and AED ${fmt(refund)} refunded to the client’s wallet${Math.abs(delta) > refund + 0.005 ? ' — the rest was never collected, so the open balance just shrinks' : ''}.`;
            }
            if (rail === 'wallet' && owedAfter > 0.005) {
              const qualifier = delta > 0 && owedAfter > delta + 0.005
                ? ' (this change plus the amount already pending)'
                : (delta < 0 ? ' — settling it in full; nothing is refunded, the lowered part was never collected' : '');
              return `${dir}, so invoice ${inv} will be regenerated and AED ${fmt(owedAfter)} charged from the client’s wallet and applied to it${qualifier}.`;
            }
            return `${dir}, so invoice ${inv} will be regenerated — `
              + (owedAfter > 0.005 ? `AED ${fmt(owedAfter)} will be open on it to collect` : 'nothing will remain to collect')
              + (delta < 0 ? '; nothing is refunded (this amount was never collected)' : '')
              + '.';
          })() : ''}
          onConfirm={() => handleSubmit({ preventDefault: () => {} })}
          onCancel={() => setConfirmRegen(false)}
        />


        <button type="button" className="delete-button" onClick={handleDelete}>Delete Booking</button>
      </div>

      <LoadingPopup isLoading={false} feedbackMessage={feedbackMessage}
        onClose={() => {
          setFeedbackMessage('');
          // Close the edit modal whenever the booking was saved (success, card-paid,
          // invoice-later, adjustment, or the not-charged warning).
          if (feedbackMessage.startsWith('Booking updated') || feedbackMessage.startsWith('⚠️ Booking saved')) onClose();
          else if (feedbackMessage === 'Booking deleted successfully!') navigate('/bookings');
        }} />
    </div>
  );
}

export default EditBooking;
